// src/components/Contratos.jsx
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../../../Supabase/SupabaseClient";
import {
  ESTADOS,
  ESTADO_LABELS,
  CAMPOS_CLIENTE,
  ETIQUETAS_CLIENTE,
  obtenerServicio,
  obtenerMonto,
  datosFaltantes,
  generarContratoPDF,
  contactarClientePorDatos,
} from "./contratoPdf";
import "./Contratos.css";

const Contratos = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  const [generandoId, setGenerandoId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Modal: ver / editar
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalTipo, setModalTipo] = useState(null); // 'ver' | 'editar'
  const [modalTab, setModalTab] = useState("orden"); // 'orden' | 'cliente' | 'contrato'
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [catalogoServicios, setCatalogoServicios] = useState([]);

  useEffect(() => {
    const fetchOrdenes = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Traigo las órdenes (sin embed) para evitar problemas con múltiples FK
        const { data: ordenesData, error: ordErr } = await supabase
          .from("ordenes")
          .select(`
            id,
            usuario_id,
            servicio_id,
            fecha_creacion,
            montoTotal,
            metodoPago,
            estado,
            cantidad,
            precio_unitario
          `)
          .order("fecha_creacion", { ascending: false });

        if (ordErr) {
          console.error("Error obteniendo órdenes:", ordErr);
          throw ordErr;
        }

        const ordenesList = ordenesData || [];

        // 2) Recolecto IDs únicos de usuarios
        const usuarioIds = Array.from(new Set(ordenesList.map((o) => o.usuario_id).filter(Boolean)));

        // 3) Traigo los usuarios
        let usuarios = [];
        if (usuarioIds.length > 0) {
          const { data: usuariosData, error: usuariosErr } = await supabase
            .from("usuarios")
            .select("id, nombre, email, dni, telefono, direccion")
            .in("id", usuarioIds);

          if (usuariosErr) {
            console.warn("Error al traer usuarios relacionados:", usuariosErr);
          } else {
            usuarios = usuariosData || [];
          }
        }

        // 4) Traigo el catálogo completo de servicios (para resolver los ya contratados
        //    y para poder asignar uno desde el admin)
        const { data: serviciosData, error: serviciosErr } = await supabase
          .from("servicios")
          .select("id, nombre, descripcion, precio")
          .order("nombre", { ascending: true });

        if (serviciosErr) {
          console.warn("Error al traer servicios:", serviciosErr);
        }

        const servicios = serviciosData || [];
        setCatalogoServicios(servicios);

        // 5) Mapas para lookup rápido
        const usuariosMap = Object.fromEntries((usuarios || []).map((u) => [u.id, u]));
        const serviciosMap = Object.fromEntries(servicios.map((s) => [s.id, s]));

        // 6) Enriquecer órdenes con usuario y servicio
        const ordenesEnriquecidas = ordenesList.map((o) => ({
          ...o,
          usuarios: usuariosMap[o.usuario_id] || null,
          servicios: serviciosMap[o.servicio_id] || null,
        }));

        setOrdenes(ordenesEnriquecidas);
      } catch (err) {
        console.error("Error en fetchOrdenes:", err);
        setError(err?.message || "Error al cargar órdenes.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrdenes();
  }, []);

  const safeDate = (val) => {
    if (!val) return "—";
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime()) && /[-T]/.test(String(val))) {
      return parsed.toLocaleDateString("es-AR");
    }
    return String(val);
  };

  const formatCurrency = (n) => {
    const num = Number(n ?? 0);
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const obtenerNombreCliente = (orden) => {
    if (!orden) return "—";

    if (orden.usuarios) {
      if (orden.usuarios.nombre && orden.usuarios.nombre.trim() !== "") {
        return orden.usuarios.nombre.trim();
      }
      if (orden.usuarios.email && orden.usuarios.email.trim() !== "") {
        return orden.usuarios.email.trim();
      }
    }

    return `Usuario ${orden.usuario_id?.slice(-4) || "####"}`;
  };

  const estadosPresentes = useMemo(() => {
    const set = new Set(ordenes.map((o) => o.estado).filter(Boolean));
    return ESTADOS.filter((e) => set.has(e)).concat(Array.from(set).filter((e) => !ESTADOS.includes(e)));
  }, [ordenes]);

  const ordenesFiltradas = useMemo(() => {
    const term = busqueda.trim().toLowerCase();

    return ordenes.filter((o) => {
      if (filtroEstado && o.estado !== filtroEstado) return false;
      if (!term) return true;

      const campos = [
        obtenerNombreCliente(o),
        o.usuarios?.email,
        o.usuarios?.dni,
        o.servicios?.nombre,
        o.metodoPago,
        `#${o.id}`,
      ];

      return campos.some((c) => c && String(c).toLowerCase().includes(term));
    });
  }, [ordenes, busqueda, filtroEstado]);

  const abrirModal = (tipo, orden = null) => {
    setModalTipo(tipo);
    setOrdenSeleccionada(orden ? { ...orden } : null);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setModalTipo(null);
    setOrdenSeleccionada(null);
    setGuardando(false);
  };

  const generarContrato = async (orden) => {
    try {
      setGenerandoId(orden.id);
      generarContratoPDF(orden);
    } catch (err) {
      console.error("Error generando contrato:", err);
      alert("Ocurrió un error al generar el contrato. Revisa la consola.");
    } finally {
      setGenerandoId(null);
    }
  };

  const eliminarOrden = async (id) => {
    const ok = window.confirm("¿Estás seguro que querés eliminar esta orden? Esta acción no se puede deshacer.");
    if (!ok) return;

    setDeletingId(id);
    try {
      const { error } = await supabase
        .from("ordenes")
        .delete()
        .eq("id", id)
        .select();

      if (error) {
        console.error("Error de Supabase al eliminar:", error);
        throw error;
      }

      setOrdenes((prev) => prev.filter((o) => o.id !== id));

      if (ordenSeleccionada?.id === id) {
        cerrarModal();
      }
    } catch (err) {
      console.error("Error eliminando orden:", err);
      alert("Error al eliminar la orden: " + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  const guardarEdicion = async () => {
    if (!ordenSeleccionada?.id) return;
    setGuardando(true);
    try {
      const orden = ordenSeleccionada;

      const payload = {
        estado: orden.estado || null,
        metodoPago: orden.metodoPago || null,
        montoTotal: orden.montoTotal,
        cantidad: orden.cantidad,
        precio_unitario: orden.precio_unitario,
        servicio_id: orden.servicio_id || null,
      };

      // 1) Datos del cliente (tabla usuarios)
      const clientePayload = {};
      CAMPOS_CLIENTE.forEach((campo) => {
        const valor = (orden.usuarios?.[campo] ?? "").trim();
        clientePayload[campo] = valor === "" ? null : valor;
      });

      let clienteGuardado = orden.usuarios || null;
      if (orden.usuario_id) {
        const { data: clienteData, error: clienteErr } = await supabase
          .from("usuarios")
          .update(clientePayload)
          .eq("id", orden.usuario_id)
          .select("id, nombre, email, dni, telefono, direccion")
          .single();

        if (clienteErr) {
          console.error("Error al actualizar el cliente:", clienteErr);
          throw clienteErr;
        }
        clienteGuardado = clienteData;
      }

      // 2) Datos de la orden
      const { data, error } = await supabase
        .from("ordenes")
        .update(payload)
        .eq("id", orden.id)
        .select(`
          id,
          usuario_id,
          servicio_id,
          fecha_creacion,
          montoTotal,
          metodoPago,
          estado,
          cantidad,
          precio_unitario
        `);

      if (error) {
        console.error("Error de Supabase al actualizar:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error("No se pudo actualizar la orden. Verifica tus permisos.");
      }

      // Mantener las relaciones ya resueltas en la UI
      const fila = data[0];
      const servicioActualizado = catalogoServicios.find((s) => s.id === fila.servicio_id) || null;

      setOrdenes((prev) =>
        prev.map((o) =>
          o.id === orden.id
            ? { ...o, ...fila, usuarios: clienteGuardado, servicios: servicioActualizado }
            : o
        )
      );

      cerrarModal();
    } catch (err) {
      console.error("Error guardando orden:", err);
      alert("Error al guardar cambios: " + (err.message || err));
    } finally {
      setGuardando(false);
    }
  };

  // Abre WhatsApp (o mail) con el cliente para pedirle los datos que faltan
  const contactarCliente = (orden) => {
    const canal = contactarClientePorDatos(orden);

    if (!canal) {
      alert("Este cliente no tiene teléfono ni email cargados. Pedile que los complete en su perfil o agregalos vos desde la pestaña Cliente.");
    }
  };

  return (
    <div className="contratos">
      <div className="contratos__header">
        <h2 className="contratos__title">Órdenes y Contratos</h2>
      </div>

      {!loading && !error && ordenes.length > 0 && (
        <div className="contratos__toolbar">
          <input
            type="search"
            className="contratos__input"
            placeholder="Buscar por cliente, DNI, servicio o Nº de orden…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar órdenes"
          />

          <select
            className="contratos__select"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            {estadosPresentes.map((e) => (
              <option key={e} value={e}>
                {ESTADO_LABELS[e] || e}
              </option>
            ))}
          </select>

          <span className="contratos__count">
            {ordenesFiltradas.length} de {ordenes.length}
          </span>
        </div>
      )}

      {loading ? (
        <div className="contratos__card" style={{ textAlign: "center" }}>Cargando órdenes…</div>
      ) : error ? (
        <div className="contratos__error">{error}</div>
      ) : ordenes.length === 0 ? (
        <div className="contratos__empty">No hay órdenes registradas.</div>
      ) : ordenesFiltradas.length === 0 ? (
        <div className="contratos__empty">No hay órdenes que coincidan con el filtro.</div>
      ) : (
        <ul className="contratos__list">
          {ordenesFiltradas.map((o) => {
            const fecha = safeDate(o.fecha_creacion);
            const cliente = obtenerNombreCliente(o);
            const servicio = obtenerServicio(o);
            const monto = obtenerMonto(o);
            const faltan = datosFaltantes(o);

            return (
              <li className="contratos__card" key={o.id}>
                <div className="contratos__card-row">
                  <div>
                    <div className="contratos__strong">Orden #{o.id}</div>
                    <div className="contratos__meta">Creada: {fecha}</div>
                  </div>

                  <div className="u-justify-end">
                    <div className="contratos__meta">
                      Estado:{" "}
                      <strong className={`contratos__estado contratos__estado--${o.estado || "pendiente"}`}>
                        {ESTADO_LABELS[o.estado] || o.estado || "—"}
                      </strong>
                    </div>
                    <div className="contratos__meta">Total: <strong>{formatCurrency(monto)}</strong></div>
                  </div>
                </div>

                <div className="contratos__card-row">
                  <div className="contratos__small"><strong>Cliente:</strong> {cliente}</div>
                  <div className="contratos__small">
                    <strong>Servicio:</strong>{" "}
                    {servicio?.nombre || <em className="contratos__warn">Sin servicio asignado</em>}
                  </div>
                  <div className="contratos__small"><strong>Método:</strong> {o.metodoPago || "—"}</div>
                </div>

                {faltan.length > 0 && (
                  <div className="contratos__faltantes">
                    <span className="contratos__faltantes-title">
                      Faltan {faltan.length} dato{faltan.length > 1 ? "s" : ""}:
                    </span>{" "}
                    {faltan.join(" · ")}
                    <button
                      type="button"
                      className="contratos__faltantes-link"
                      onClick={() => contactarCliente(o)}
                    >
                      Contactar al cliente
                    </button>
                  </div>
                )}

                <div className="contratos__actions">
                  <button
                    className="contratos__btn contratos__btn--primary"
                    onClick={() => generarContrato(o)}
                    disabled={generandoId === o.id}
                  >
                    {generandoId === o.id ? "Generando..." : "Generar Contrato"}
                  </button>

                  <button
                    className="contratos__btn contratos__btn--ghost"
                    onClick={() => abrirModal("ver", o)}
                  >
                    Ver detalle
                  </button>

                  <button
                    className="contratos__btn contratos__btn--ghost"
                    onClick={() => abrirModal("editar", o)}
                  >
                    Editar
                  </button>

                  <button
                    className="contratos__btn contratos__btn--ghost"
                    onClick={() => eliminarOrden(o.id)}
                    disabled={deletingId === o.id}
                  >
                    {deletingId === o.id ? "Eliminando..." : "Eliminar"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal ver / editar */}
      {modalAbierto && ordenSeleccionada && (
        <div
          className="contratos__modal-overlay"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && cerrarModal()}
        >
          <div className="contratos__modal" role="dialog" aria-modal="true" aria-labelledby="contratos-modal-title">
            <div className="contratos__modal-header">
              <div>
                <p className="contratos__modal-eyebrow">Orden #{ordenSeleccionada.id}</p>
                <h3 className="contratos__modal-title" id="contratos-modal-title">
                  {modalTipo === "ver" ? "Detalle del contrato" : "Editar orden"}
                </h3>
              </div>
              <button onClick={cerrarModal} className="contratos__modal-close" aria-label="Cerrar">×</button>
            </div>

            <div className="contratos__modal-body">
              {datosFaltantes(ordenSeleccionada).length > 0 && (
                <div className="contratos__modal-alert">
                  <div className="contratos__modal-alert-text">
                    <strong>Faltan datos para completar el contrato:</strong>{" "}
                    {datosFaltantes(ordenSeleccionada).join(" · ")}
                  </div>
                  <button
                    type="button"
                    className="contratos__modal-alert-btn"
                    onClick={() => contactarCliente(ordenSeleccionada)}
                  >
                    Contactar al cliente
                  </button>
                </div>
              )}

              {modalTipo === "ver" ? (
                <>
                  <div className="contratos__modal-summary">
                    <div>
                      <span className="contratos__modal-summary-label">Cliente</span>
                      <strong className="contratos__modal-summary-value">
                        {obtenerNombreCliente(ordenSeleccionada)}
                      </strong>
                    </div>
                    <div>
                      <span className="contratos__modal-summary-label">Servicio</span>
                      <strong className="contratos__modal-summary-value">
                        {obtenerServicio(ordenSeleccionada)?.nombre || "Sin servicio asignado"}
                      </strong>
                    </div>
                    <div>
                      <span className="contratos__modal-summary-label">Total</span>
                      <strong className="contratos__modal-summary-value contratos__modal-summary-value--mg">
                        {formatCurrency(obtenerMonto(ordenSeleccionada))}
                      </strong>
                    </div>
                  </div>

                  <section className="contratos__modal-section">
                    <h4 className="contratos__modal-section-title">Datos del cliente</h4>
                    <dl className="contratos__datalist">
                      <div className="contratos__datalist-row">
                        <dt>Email</dt>
                        <dd>{ordenSeleccionada.usuarios?.email || "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>DNI</dt>
                        <dd>{ordenSeleccionada.usuarios?.dni || "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Teléfono</dt>
                        <dd>{ordenSeleccionada.usuarios?.telefono || "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Dirección</dt>
                        <dd>{ordenSeleccionada.usuarios?.direccion || "—"}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="contratos__modal-section">
                    <h4 className="contratos__modal-section-title">Servicio contratado</h4>
                    <dl className="contratos__datalist">
                      <div className="contratos__datalist-row">
                        <dt>Nombre</dt>
                        <dd>{obtenerServicio(ordenSeleccionada)?.nombre || "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Precio de lista</dt>
                        <dd>
                          {obtenerServicio(ordenSeleccionada)?.precio != null
                            ? formatCurrency(obtenerServicio(ordenSeleccionada).precio)
                            : "—"}
                        </dd>
                      </div>
                      <div className="contratos__datalist-row contratos__datalist-row--full">
                        <dt>Incluye</dt>
                        <dd>{obtenerServicio(ordenSeleccionada)?.descripcion || "—"}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="contratos__modal-section">
                    <h4 className="contratos__modal-section-title">Pago</h4>
                    <dl className="contratos__datalist">
                      <div className="contratos__datalist-row">
                        <dt>Estado</dt>
                        <dd>
                          <span className={`contratos__estado contratos__estado--${ordenSeleccionada.estado || "pendiente"}`}>
                            {ESTADO_LABELS[ordenSeleccionada.estado] || ordenSeleccionada.estado || "—"}
                          </span>
                        </dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Método</dt>
                        <dd>{ordenSeleccionada.metodoPago || "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Cantidad</dt>
                        <dd>{ordenSeleccionada.cantidad ?? "—"}</dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Precio unitario</dt>
                        <dd>
                          {ordenSeleccionada.precio_unitario != null
                            ? formatCurrency(ordenSeleccionada.precio_unitario)
                            : "—"}
                        </dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Monto total</dt>
                        <dd>
                          {ordenSeleccionada.montoTotal != null
                            ? formatCurrency(ordenSeleccionada.montoTotal)
                            : "—"}
                        </dd>
                      </div>
                      <div className="contratos__datalist-row">
                        <dt>Fecha de creación</dt>
                        <dd>{safeDate(ordenSeleccionada.fecha_creacion)}</dd>
                      </div>
                    </dl>
                  </section>
                </>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); guardarEdicion(); }}>
                  <div className="contratos__tabs" role="tablist">
                    {[
                      { id: "orden", label: "Orden" },
                      { id: "cliente", label: "Cliente" },
                      { id: "servicio", label: "Servicio" },
                    ].map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={modalTab === t.id}
                        className={`contratos__tab ${modalTab === t.id ? "contratos__tab--active" : ""}`}
                        onClick={() => setModalTab(t.id)}
                      >
                        {t.label}
                        {t.id === "cliente" && datosFaltantes(ordenSeleccionada).length > 0 && (
                          <span className="contratos__tab-dot" aria-hidden="true" />
                        )}
                      </button>
                    ))}
                  </div>

                  {modalTab === "orden" && (
                    <div className="contratos__form">
                      <div className="contratos__form-group">
                        <label className="contratos__form-label" htmlFor="contratos-estado">Estado</label>
                        <select
                          id="contratos-estado"
                          className="contratos__form-select"
                          value={ordenSeleccionada.estado || ""}
                          onChange={(e) => setOrdenSeleccionada({ ...ordenSeleccionada, estado: e.target.value })}
                        >
                          <option value="">Sin estado</option>
                          {ESTADOS.map((e) => (
                            <option key={e} value={e}>
                              {ESTADO_LABELS[e]}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="contratos__form-group">
                        <label className="contratos__form-label" htmlFor="contratos-metodo">Método de pago</label>
                        <input
                          id="contratos-metodo"
                          className="contratos__form-input"
                          placeholder="Transferencia, efectivo, alias…"
                          value={ordenSeleccionada.metodoPago || ""}
                          onChange={(e) => setOrdenSeleccionada({ ...ordenSeleccionada, metodoPago: e.target.value })}
                        />
                      </div>

                      <div className="contratos__form-group">
                        <label className="contratos__form-label" htmlFor="contratos-cantidad">Cantidad</label>
                        <input
                          id="contratos-cantidad"
                          className="contratos__form-input"
                          type="number"
                          step="1"
                          min="0"
                          value={ordenSeleccionada.cantidad != null ? ordenSeleccionada.cantidad : ""}
                          onChange={(e) => setOrdenSeleccionada({ ...ordenSeleccionada, cantidad: e.target.value ? Number(e.target.value) : null })}
                        />
                      </div>

                      <div className="contratos__form-group">
                        <label className="contratos__form-label" htmlFor="contratos-unitario">Precio unitario</label>
                        <input
                          id="contratos-unitario"
                          className="contratos__form-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={ordenSeleccionada.precio_unitario != null ? ordenSeleccionada.precio_unitario : ""}
                          onChange={(e) => setOrdenSeleccionada({ ...ordenSeleccionada, precio_unitario: e.target.value ? Number(e.target.value) : null })}
                        />
                      </div>

                      <div className="contratos__form-group contratos__form-group--full">
                        <label className="contratos__form-label" htmlFor="contratos-monto">Monto total (USD)</label>
                        <input
                          id="contratos-monto"
                          className="contratos__form-input contratos__form-input--lg"
                          type="number"
                          step="0.01"
                          min="0"
                          value={ordenSeleccionada.montoTotal != null ? ordenSeleccionada.montoTotal : ""}
                          onChange={(e) => setOrdenSeleccionada({ ...ordenSeleccionada, montoTotal: e.target.value ? Number(e.target.value) : null })}
                        />
                        <p className="contratos__form-hint">
                          Calculado por cantidad × unitario:{" "}
                          {formatCurrency(
                            (Number(ordenSeleccionada.precio_unitario) || 0) * (Number(ordenSeleccionada.cantidad) || 0)
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {modalTab === "cliente" && (
                    <div className="contratos__form">
                      {CAMPOS_CLIENTE.map((campo) => {
                        const falta = !(ordenSeleccionada.usuarios?.[campo] || "").trim();
                        return (
                          <div
                            className="contratos__form-group"
                            key={campo}
                          >
                            <label className="contratos__form-label" htmlFor={`contratos-cli-${campo}`}>
                              {ETIQUETAS_CLIENTE[campo]}
                              {falta && <span className="contratos__form-missing">Falta</span>}
                            </label>
                            <input
                              id={`contratos-cli-${campo}`}
                              className={`contratos__form-input ${falta ? "contratos__form-input--missing" : ""}`}
                              type={campo === "email" ? "email" : "text"}
                              placeholder={falta ? "Pendiente de confirmar con el cliente" : ""}
                              value={ordenSeleccionada.usuarios?.[campo] || ""}
                              onChange={(e) => setOrdenSeleccionada({
                                ...ordenSeleccionada,
                                usuarios: { ...(ordenSeleccionada.usuarios || {}), [campo]: e.target.value },
                              })}
                            />
                          </div>
                        );
                      })}

                      <div className="contratos__form-group contratos__form-group--full">
                        <p className="contratos__form-hint">
                          Los datos se guardan en el perfil del cliente, así que quedan disponibles
                          para todos sus contratos y órdenes.
                        </p>
                      </div>
                    </div>
                  )}

                  {modalTab === "servicio" && (
                    <div className="contratos__form">
                      <div className="contratos__form-group contratos__form-group--full">
                        <label className="contratos__form-label" htmlFor="contratos-servicio">
                          Servicio contratado
                        </label>
                        <select
                          id="contratos-servicio"
                          className="contratos__form-select"
                          value={ordenSeleccionada.servicio_id || ""}
                          onChange={(e) => {
                            const id = e.target.value === "" ? null : Number(e.target.value);
                            const svc = catalogoServicios.find((s) => s.id === id) || null;
                            setOrdenSeleccionada({ ...ordenSeleccionada, servicio_id: id, servicios: svc });
                          }}
                        >
                          <option value="">— Sin servicio asignado —</option>
                          {catalogoServicios.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nombre}
                              {s.precio != null ? ` — ${formatCurrency(s.precio)}` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="contratos__form-hint">
                          El precio de catálogo no pisa lo que pagó el cliente: el contrato usa el
                          monto de la orden.
                        </p>
                      </div>

                      {obtenerServicio(ordenSeleccionada)?.descripcion && (
                        <div className="contratos__form-group contratos__form-group--full">
                          <span className="contratos__form-label">Lo que incluye (va a la cláusula TERCERA del PDF)</span>
                          <p className="contratos__form-descripcion">{obtenerServicio(ordenSeleccionada).descripcion}</p>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              )}
            </div>

            <div className="contratos__modal-actions">
              <button onClick={cerrarModal} className="contratos__modal-btn contratos__modal-btn--cancel" disabled={guardando}>
                Cerrar
              </button>

              {modalTipo === "ver" ? (
                <>
                  <button
                    onClick={() => generarContrato(ordenSeleccionada)}
                    className="contratos__modal-btn contratos__modal-btn--ghost"
                  >
                    Generar PDF
                  </button>
                  <button
                    onClick={() => abrirModal("editar", ordenSeleccionada)}
                    className="contratos__modal-btn contratos__modal-btn--save"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => eliminarOrden(ordenSeleccionada.id)}
                    className="contratos__modal-btn contratos__modal-btn--danger"
                  >
                    Eliminar
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => eliminarOrden(ordenSeleccionada.id)}
                    className="contratos__modal-btn contratos__modal-btn--danger"
                    disabled={guardando}
                  >
                    Eliminar
                  </button>
                  <button
                    onClick={guardarEdicion}
                    className="contratos__modal-btn contratos__modal-btn--save"
                    disabled={guardando}
                  >
                    {guardando ? "Guardando..." : "Guardar cambios"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contratos;
